import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contato - JampaBit Informática João Pessoa',
  description: 'Entre em contato com a JampaBit Informática em João Pessoa. Solicite orçamento, tire dúvidas ou agende um atendimento de suporte técnico em TI.',
  url: `${import.meta.env.VITE_SITE_URL}/contato`,
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'JampaBit Informática',
    telephone: '+55-83-99175-5663',
    email: 'jampabit@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'João Pessoa',
      addressRegion: 'PB',
      addressCountry: 'BR',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '08:00',
        closes: '13:00',
      },
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+55-83-99175-5663',
      contactType: 'customer service',
      availableLanguage: 'Portuguese',
      areaServed: 'BR',
    },
  },
};

export default function ContatoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contato';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-contato');
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d77ss1r86jhav3jpeuag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#1a3a4a] pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 border border-[#00c9a7]/40 text-[#00c9a7] text-xs font-semibold px-4 py-2 rounded-full mb-5 md:mb-6">
            <i className="ri-mail-line" />
            Fale Conosco
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-5">
            Entre em <span className="text-[#00c9a7]">Contato</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto">
            Solicite um orçamento, tire dúvidas ou agende um atendimento. Respondemos rapidamente!
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          {/* Info */}
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#0d1b2a] mb-6 md:mb-8">Informações de Contato</h2>
            <div className="space-y-5 mb-8 md:mb-10">
              {[
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '(83) 9 9175-5663', href: 'https://wa.me/5583991755663' },
                { icon: 'ri-mail-line', label: 'E-mail', value: 'jampabit@gmail.com', href: 'mailto:jampabit@gmail.com' },
                { icon: 'ri-map-pin-line', label: 'Localização', value: 'João Pessoa, Paraíba — Atendimento a domicílio', href: null },
                { icon: 'ri-time-line', label: 'Horário', value: 'Seg–Sex: 8h–18h | Sáb: 8h–13h', href: null },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#00c9a7]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#00c9a7] text-lg`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium mb-0.5">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-[#0d1b2a] font-semibold text-sm hover:text-[#00c9a7] transition-colors cursor-pointer">{item.value}</a>
                    ) : (
                      <span className="text-[#0d1b2a] font-semibold text-sm">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://wa.me/5583991755663?text=Olá!%20Gostaria%20de%20solicitar%20um%20atendimento%20da%20JampaBit."
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-6 md:px-7 py-3.5 md:py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-whatsapp-line text-lg" />
              Chamar no WhatsApp Agora
            </a>

            <div className="mt-8 md:mt-10 rounded-2xl overflow-hidden h-48 md:h-56">
              <iframe
                title="JampaBit - João Pessoa"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124699.37!2d-34.9286!3d-7.1195!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7ace836898b4a5b%3A0x3a7b3e2e2e2e2e2e!2sJo%C3%A3o%20Pessoa%2C%20PB!5e0!3m2!1spt-BR!2sbr!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#f8fafb] rounded-2xl p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-[#0d1b2a] mb-2">Envie uma Mensagem</h2>
            <p className="text-gray-500 text-sm mb-6 md:mb-8">Preencha o formulário e retornaremos em breve.</p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00c9a7]/15 mb-5">
                  <i className="ri-check-line text-[#00c9a7] text-3xl" />
                </div>
                <h3 className="text-[#0d1b2a] font-bold text-xl mb-2">Mensagem Enviada!</h3>
                <p className="text-gray-500 text-sm">Obrigado pelo contato. Retornaremos em breve!</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Nome *</label>
                    <input name="nome" required type="text" placeholder="Seu nome completo" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#00c9a7] bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Telefone *</label>
                    <input name="telefone" required type="tel" placeholder="(83) 9 9999-9999" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#00c9a7] bg-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">E-mail *</label>
                  <input name="email" required type="email" placeholder="seu@email.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#00c9a7] bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Tipo de Atendimento</label>
                  <select name="tipo_atendimento" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#00c9a7] bg-white cursor-pointer">
                    <option value="">Selecione um serviço</option>
                    <option value="Formatação">Formatação de Micro/Notebook</option>
                    <option value="Montagem PC Gamer">Montagem de PC Gamer</option>
                    <option value="Limpeza">Limpeza e Manutenção</option>
                    <option value="Suporte Remoto">Suporte Remoto</option>
                    <option value="Instalação de Software">Instalação de Software</option>
                    <option value="Consultoria TI">Consultoria em TI</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Mensagem *</label>
                  <textarea
                    name="mensagem"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Descreva seu problema ou dúvida..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#00c9a7] bg-white resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#00c9a7] hover:bg-[#00b396] disabled:opacity-60 text-white font-semibold py-3.5 md:py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                  {loading ? 'Enviando...' : 'Enviar Mensagem'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
